import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { format } from "date-fns";
import { FileSpreadsheet, Info, Printer, RotateCcw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { DateField } from "@/components/calculator/DateField";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  computeEMI,
  scheduleToCSV,
  validateEMI,
  type EMIError,
} from "@/lib/calculators/emi";
import {
  BD_BANKS,
  LOAN_TYPES,
  RATES_DISCLAIMER,
  type LoanType,
} from "@/data/bdBankRates";

/* ---------- Strings ---------- */

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "EMI Calculator", bn: "ইএমআই ক্যালকুলেটর" },
  desc: {
    en: "Calculate monthly EMI for any Bangladeshi bank loan — home, car, motorcycle, personal or business. Includes amortization schedule and BD bank rate presets.",
    bn: "যেকোনো বাংলাদেশী ব্যাংক ঋণের মাসিক ইএমআই হিসাব করুন — হোম, কার, মোটরসাইকেল, পার্সোনাল বা বিজনেস। সম্পূর্ণ অ্যামোর্টাইজেশন সিডিউল ও বিডি ব্যাংক রেট প্রিসেট সহ।",
  },
  loanType: { en: "Loan type", bn: "ঋণের ধরন" },
  bank: { en: "Bank preset", bn: "ব্যাংক প্রিসেট" },
  bankRateNote: {
    en: "Indicative rate — verify with bank before applying.",
    bn: "প্রদর্শিত হার ধারণা মাত্র — আবেদনের আগে ব্যাংকের সঙ্গে যাচাই করুন।",
  },
  loanAmount: { en: "Loan amount", bn: "ঋণের পরিমাণ" },
  rate: { en: "Annual interest rate", bn: "বার্ষিক সুদের হার" },
  tenure: { en: "Tenure", bn: "মেয়াদ" },
  years: { en: "Years", bn: "বছর" },
  months: { en: "Months", bn: "মাস" },
  startDate: { en: "Start date", bn: "শুরুর তারিখ" },
  reset: { en: "Reset", bn: "রিসেট" },

  monthlyEMI: { en: "Monthly EMI", bn: "মাসিক ইএমআই" },
  totalInterest: { en: "Total interest", bn: "মোট সুদ" },
  totalPayment: { en: "Total payment", bn: "মোট পরিশোধ" },
  principal: { en: "Principal", bn: "মূলধন" },
  interest: { en: "Interest", bn: "সুদ" },

  yearly: { en: "Yearly", bn: "বার্ষিক" },
  monthly: { en: "Monthly", bn: "মাসিক" },
  schedule: { en: "Amortization schedule", bn: "অ্যামোর্টাইজেশন সিডিউল" },
  year: { en: "Year", bn: "বছর" },
  month: { en: "Month", bn: "মাস" },
  date: { en: "Date", bn: "তারিখ" },
  emi: { en: "EMI", bn: "ইএমআই" },
  principalPaid: { en: "Principal paid", bn: "পরিশোধিত মূলধন" },
  interestPaid: { en: "Interest paid", bn: "পরিশোধিত সুদ" },
  balance: { en: "Balance", bn: "অবশিষ্ট" },

  downloadCsv: { en: "Download CSV", bn: "CSV ডাউনলোড" },
  print: { en: "Print / PDF", bn: "প্রিন্ট / PDF" },
  bnNumerals: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },

  // Errors
  err_amount_too_small: { en: "Loan amount too small (min ৳1,000).", bn: "ঋণের পরিমাণ খুব কম (সর্বনিম্ন ৳১,০০০)।" },
  err_tenure_zero: { en: "Tenure must be at least 1 month.", bn: "মেয়াদ অন্তত ১ মাস হতে হবে।" },
  err_rate_negative: { en: "Interest rate cannot be negative.", bn: "সুদের হার ঋণাত্মক হতে পারে না।" },
  err_zero_warn: {
    en: "Zero interest loan — verify terms with the lender.",
    bn: "শূন্য সুদের ঋণ — শর্তাবলী যাচাই করুন।",
  },

  // Content
  whatTitle: { en: "What is EMI?", bn: "ইএমআই কী?" },
  whatBody: {
    en: "EMI (Equated Monthly Installment) is the fixed payment a borrower pays to a lender each month so the loan and its interest are fully repaid by the end of the tenure. Each EMI is part interest on the outstanding balance and part principal — early months are mostly interest, later months are mostly principal.",
    bn: "ইএমআই (Equated Monthly Installment) হলো মাসিক একই পরিমাণ অর্থ যা ঋণগ্রহীতা ব্যাংককে প্রদান করেন, যাতে মেয়াদ শেষে ঋণ ও সুদ সম্পূর্ণ পরিশোধ হয়। প্রতিটি ইএমআইয়ের কিছু অংশ অবশিষ্ট ঋণের সুদ এবং বাকিটা মূল ঋণ — শুরুর মাসগুলোতে বেশি সুদ, পরে বেশি মূলধন কাটে।",
  },
  formulaTitle: { en: "EMI formula", bn: "ইএমআই সূত্র" },
  formulaBody: {
    en: "EMI = P × R × (1 + R)ⁿ ÷ [(1 + R)ⁿ − 1], where P is principal, R is monthly interest rate (annual % ÷ 12 ÷ 100), and n is the number of months. Example: ৳5,00,000 at 12% for 5 years (60 months) → R = 0.01, EMI ≈ ৳11,122.",
    bn: "ইএমআই = P × R × (১ + R)ⁿ ÷ [(১ + R)ⁿ − ১] — যেখানে P মূলধন, R মাসিক সুদের হার (বার্ষিক % ÷ ১২ ÷ ১০০), এবং n মাসের সংখ্যা। উদাহরণ: ৳৫,০০,০০০ ১২% সুদে ৫ বছরের (৬০ মাস) ঋণে EMI ≈ ৳১১,১২২।",
  },
  reduceTitle: { en: "How to reduce your EMI", bn: "ইএমআই কীভাবে কমাবেন" },
  reduceItems: {
    en: [
      "Choose a longer tenure — monthly payment falls but total interest rises.",
      "Pay a larger down-payment so the principal is smaller.",
      "Improve your credit score / banking history to negotiate a lower rate.",
      "Refinance via balance transfer to a bank offering a lower rate.",
      "Make periodic part-prepayments to cut the outstanding principal early.",
    ],
    bn: [
      "মেয়াদ বাড়ান — মাসিক কিস্তি কমবে, তবে মোট সুদ বাড়বে।",
      "বেশি ডাউন-পেমেন্ট দিন যেন মূলধন কম হয়।",
      "ক্রেডিট স্কোর / ব্যাংকিং রেকর্ড উন্নত করে কম রেট নেগোশিয়েট করুন।",
      "কম সুদের অন্য ব্যাংকে ব্যালান্স ট্রান্সফার করুন।",
      "মাঝে মাঝে আংশিক প্রি-পেমেন্ট দিয়ে অবশিষ্ট মূলধন কমান।",
    ],
  },
  flatTitle: { en: "EMI vs Flat interest", bn: "ইএমআই বনাম ফ্ল্যাট সুদ" },
  flatBody: {
    en: "Many BD micro-lenders quote 'flat' rates that look low — for example 8% flat over 3 years. Flat interest is calculated on the original principal for the whole tenure, so the effective reducing-balance rate (which banks and EMI calculators use) is roughly 1.8× the flat rate. Always compare on a reducing-balance basis.",
    bn: "অনেক বিডি মাইক্রো-লেন্ডার কম মনে হওয়া 'ফ্ল্যাট' হার দেখায় — যেমন ৩ বছরের জন্য ৮% ফ্ল্যাট। ফ্ল্যাট সুদ পুরো মেয়াদে মূল ঋণের উপর হিসাব হয়, তাই কার্যকর রিডিউসিং-ব্যালান্স হার (যা ব্যাংক ও ইএমআই ক্যালকুলেটর ব্যবহার করে) প্রায় ১.৮ গুণ বেশি হয়। সবসময় রিডিউসিং-ব্যালান্স ভিত্তিতে তুলনা করুন।",
  },
  ratesTitle: { en: "Bangladesh bank loan rates 2026", bn: "বাংলাদেশ ব্যাংক ঋণ হার ২০২৬" },
  taxTitle: { en: "Tax benefits on home loan EMI in Bangladesh", bn: "হোম লোন ইএমআইতে কর সুবিধা (বাংলাদেশ)" },
  taxBody: {
    en: "Under Bangladesh's Income Tax Act, individual taxpayers can claim a deduction on interest paid for a self-occupied housing loan, subject to ceilings set by the NBR. Keep your bank's annual interest certificate and consult a tax advisor for the current year's limits.",
    bn: "বাংলাদেশ আয়কর আইনে নিজের বসবাসের জন্য নেওয়া হাউজিং লোনের সুদের উপর ছাড় দাবি করা যায়, NBR-এর নির্ধারিত সীমা অনুযায়ী। ব্যাংকের বার্ষিক সুদের সার্টিফিকেট রাখুন এবং সর্বশেষ সীমার জন্য কর-পরামর্শকের সঙ্গে আলোচনা করুন।",
  },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How is EMI calculated?", bn: "ইএমআই কীভাবে গণনা করা হয়?" },
    a: {
      en: "Using the formula EMI = P × R × (1+R)ⁿ ÷ [(1+R)ⁿ − 1], where P is principal, R is monthly rate and n is number of months. Each month's interest is calculated on the remaining balance, and the principal portion grows over time.",
      bn: "EMI = P × R × (১+R)ⁿ ÷ [(১+R)ⁿ − ১] সূত্র ব্যবহার করে — P মূলধন, R মাসিক সুদ ও n মাসের সংখ্যা। প্রতি মাসের সুদ অবশিষ্ট ব্যালেন্সের উপর গণনা হয় এবং সময়ের সঙ্গে মূলধনের অংশ বাড়ে।",
    },
  },
  {
    q: { en: "What's a good EMI-to-income ratio in Bangladesh?", bn: "বাংলাদেশে আদর্শ ইএমআই-আয় অনুপাত কত?" },
    a: {
      en: "Most BD banks expect total monthly EMIs to stay below 50% of net monthly income. Conservative personal-finance advice keeps it under 35–40% so you have room for emergencies and savings.",
      bn: "অধিকাংশ বিডি ব্যাংক প্রত্যাশা করে মোট মাসিক EMI নিট মাসিক আয়ের ৫০%-এর নিচে থাকুক। সতর্ক পরামর্শ অনুযায়ী ৩৫–৪০%-এর নিচে রাখা ভালো, যাতে জরুরি খরচ ও সঞ্চয়ের জায়গা থাকে।",
    },
  },
  {
    q: { en: "Can I prepay my loan EMI?", bn: "আমি কি ইএমআই আগে পরিশোধ করতে পারি?" },
    a: {
      en: "Yes — most BD banks allow part- or full-prepayment after the first 6–12 months, sometimes with a 1–2% prepayment fee. Prepayment reduces the outstanding principal and therefore the total interest you pay.",
      bn: "হ্যাঁ — বেশিরভাগ বিডি ব্যাংক প্রথম ৬–১২ মাসের পর আংশিক বা সম্পূর্ণ প্রি-পেমেন্টের সুযোগ দেয়, কখনো ১–২% প্রি-পেমেন্ট ফি সহ। প্রি-পেমেন্ট অবশিষ্ট মূলধন কমিয়ে মোট সুদের পরিমাণ কমায়।",
    },
  },
  {
    q: { en: "What's the difference between EMI and flat interest?", bn: "ইএমআই ও ফ্ল্যাট সুদের মধ্যে পার্থক্য কী?" },
    a: {
      en: "EMI uses reducing-balance interest — interest only on what's left to pay. Flat interest charges on the full original principal for the whole tenure, so a 'low' flat rate is actually much higher in effective terms. Convert to reducing-balance before comparing offers.",
      bn: "EMI রিডিউসিং-ব্যালান্স সুদ ব্যবহার করে — শুধু অবশিষ্ট ঋণের উপর সুদ। ফ্ল্যাট সুদ পুরো মেয়াদে মূল ঋণের উপর হিসাব হয়, তাই 'কম' ফ্ল্যাট হার প্রকৃতপক্ষে অনেক বেশি। তুলনার আগে রিডিউসিং-ব্যালান্সে রূপান্তর করুন।",
    },
  },
  {
    q: { en: "Which BD bank has the lowest EMI?", bn: "কোন বিডি ব্যাংকের EMI সবচেয়ে কম?" },
    a: {
      en: "Lower interest rates produce lower EMIs. Standard Chartered, HSBC and large private banks like DBBL and City Bank tend to advertise the lowest personal-loan rates (around 11.5–12.5%), while NBFIs typically charge more. Final EMI also depends on the tenure you choose.",
      bn: "কম সুদের হারে EMI কম হয়। Standard Chartered, HSBC এবং বড় প্রাইভেট ব্যাংক যেমন DBBL ও City Bank সাধারণত পার্সোনাল লোনে কম রেট (১১.৫–১২.৫%) দেখায়। NBFI সাধারণত বেশি নেয়। চূড়ান্ত EMI মেয়াদের উপরও নির্ভর করে।",
    },
  },
  {
    q: { en: "ইএমআই কীভাবে কম করা যায়?", bn: "ইএমআই কীভাবে কম করা যায়?" },
    a: {
      en: "মেয়াদ বাড়ান, বেশি ডাউন-পেমেন্ট দিন, ক্রেডিট স্কোর ভালো রাখুন, কম সুদের ব্যাংকে ব্যালান্স ট্রান্সফার করুন এবং সম্ভব হলে আংশিক প্রি-পেমেন্ট দিন।",
      bn: "মেয়াদ বাড়ান, বেশি ডাউন-পেমেন্ট দিন, ক্রেডিট স্কোর ভালো রাখুন, কম সুদের ব্যাংকে ব্যালান্স ট্রান্সফার করুন এবং সম্ভব হলে আংশিক প্রি-পেমেন্ট দিন।",
    },
  },
  {
    q: { en: "হোম লোনের ইএমআই কীভাবে গণনা করা হয়?", bn: "হোম লোনের ইএমআই কীভাবে গণনা করা হয়?" },
    a: {
      en: "একই EMI সূত্র — শুধু সাধারণত মেয়াদ ১০–২৫ বছর এবং সুদের হার ৯–১২% রাখুন। উপরে 'হোম লোন' প্রিসেট চাপলে আদর্শ মান বসে যাবে।",
      bn: "একই EMI সূত্র — শুধু সাধারণত মেয়াদ ১০–২৫ বছর এবং সুদের হার ৯–১২% রাখুন। উপরে 'হোম লোন' প্রিসেট চাপলে আদর্শ মান বসে যাবে।",
    },
  },
  {
    q: { en: "মোটরসাইকেল লোনের ইএমআই কত?", bn: "মোটরসাইকেল লোনের ইএমআই কত?" },
    a: {
      en: "উদাহরণ: ৳২,০০,০০০ ১২% সুদে ৩ বছরের জন্য EMI প্রায় ৳৬,৬৪৪। উপরে 'মোটরসাইকেল লোন' প্রিসেট ব্যবহার করে নিজের সংখ্যা বসান।",
      bn: "উদাহরণ: ৳২,০০,০০০ ১২% সুদে ৩ বছরের জন্য EMI প্রায় ৳৬,৬৪৪। উপরে 'মোটরসাইকেল লোন' প্রিসেট ব্যবহার করে নিজের সংখ্যা বসান।",
    },
  },
];

/* ---------- Route ---------- */

export const Route = createFileRoute("/emi-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "EMI Calculator — Bank Loan EMI for Bangladesh",
      description:
        "Free EMI calculator with Bangladeshi bank rates (DBBL, BRAC, City Bank). Calculate monthly EMI for home loan, car loan, personal loan. ইএমআই ক্যালকুলেটর।",
      path: "/emi-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "EMI Calculator", path: "/emi-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: EMICalculatorPage,
});

/* ---------- Page ---------- */

const AMOUNT_MIN = 10_000;
const AMOUNT_MAX = 500_000_000; // 50 crore
const RATE_MIN = 0.1;
const RATE_MAX = 30;

function EMICalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const money = (n: number, frac = 0) => formatBDT(Math.round(n * 100) / 100, lang, frac);

  const [loanType, setLoanType] = useState<LoanType>("personal");
  const [bankId, setBankId] = useState<string>("custom");
  const [amount, setAmount] = useState<number>(500_000);
  const [amountStr, setAmountStr] = useState<string>("500000");
  const [rate, setRate] = useState<number>(12);
  const [rateStr, setRateStr] = useState<string>("12");
  const [tenureUnit, setTenureUnit] = useState<"years" | "months">("years");
  const [tenureValue, setTenureValue] = useState<number>(5);
  const [tenureStr, setTenureStr] = useState<string>("5");
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [bnDigits, setBnDigits] = useState(false);

  // Sync number string -> numeric
  useEffect(() => {
    const v = parseFloat(amountStr);
    if (Number.isFinite(v)) setAmount(Math.min(AMOUNT_MAX, Math.max(0, v)));
  }, [amountStr]);
  useEffect(() => {
    const v = parseFloat(rateStr);
    if (Number.isFinite(v)) setRate(Math.min(RATE_MAX, Math.max(0, v)));
  }, [rateStr]);
  useEffect(() => {
    const v = parseFloat(tenureStr);
    if (Number.isFinite(v)) setTenureValue(Math.max(1, v));
  }, [tenureStr]);

  // Apply loan-type preset
  const applyLoanType = (id: LoanType) => {
    setLoanType(id);
    const lt = LOAN_TYPES.find((l) => l.id === id);
    if (!lt) return;
    setAmount(lt.defaultAmount);
    setAmountStr(String(lt.defaultAmount));
    setRate(lt.defaultRate);
    setRateStr(String(lt.defaultRate));
    setTenureUnit("years");
    setTenureValue(lt.defaultYears);
    setTenureStr(String(lt.defaultYears));
    setBankId("custom");
  };

  // Apply bank preset (rate only, picks the rate matching current loan type)
  const applyBank = (id: string) => {
    setBankId(id);
    const bank = BD_BANKS.find((b) => b.id === id);
    if (!bank) return;
    const lt = loanType === "custom" ? "personal" : loanType;
    const r = bank.rates[lt as keyof typeof bank.rates] ?? bank.rates.personal;
    if (r != null) {
      setRate(r);
      setRateStr(String(r));
    }
  };

  const months = tenureUnit === "years" ? Math.round(tenureValue * 12) : Math.round(tenureValue);
  const validation = validateEMI({ principal: amount, annualRatePct: rate, months });
  const errKey: EMIError | null = validation.ok ? null : validation.error;

  const result = useMemo(() => {
    if (!validation.ok) return null;
    return computeEMI({ principal: amount, annualRatePct: rate, months, startDate: startDate ?? undefined });
  }, [amount, rate, months, startDate, validation.ok]);

  const reset = () => applyLoanType("personal");

  const downloadCSV = () => {
    if (!result) return;
    const csv = scheduleToCSV(result.schedule);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `emi-schedule-${amount}-${rate}pc-${months}m.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printRef = useRef<HTMLDivElement>(null);
  const doPrint = () => window.print();

  /* ----- Donut chart data ----- */
  const chartData = result
    ? [
        { name: tr("principal"), value: amount, color: "var(--accent)" },
        { name: tr("interest"), value: Math.max(0, result.totalInterest), color: "var(--muted-foreground)" },
      ]
    : [];

  const errMessage = (() => {
    if (!errKey) return null;
    const map: Record<EMIError, keyof typeof STR | null> = {
      amount_too_small: "err_amount_too_small",
      tenure_zero: "err_tenure_zero",
      rate_negative: "err_rate_negative",
      missing: null,
    };
    const k = map[errKey];
    return k ? tr(k) : null;
  })();

  const showZeroWarn = rate === 0 && validation.ok;

  return (
    <CalculatorLayout
      slug="emi-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Calculator"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <TooltipProvider delayDuration={150}>
        {/* Loan-type chips */}
        <div className="space-y-2">
          <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("loanType")}</p>
          <div className="flex flex-wrap gap-1.5">
            {LOAN_TYPES.map((lt) => {
              const active = loanType === lt.id;
              return (
                <button
                  key={lt.id}
                  type="button"
                  onClick={() => applyLoanType(lt.id)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                    active
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border bg-card text-foreground/80 hover:border-accent/40 hover:text-foreground",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {lt.label[lang]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bank preset */}
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <label className={cn("flex items-center gap-1.5 text-sm font-medium", lang === "bn" && "font-bn")}>
              {tr("bank")}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info size={13} className="text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className={cn(lang === "bn" && "font-bn")}>
                  {tr("bankRateNote")}
                </TooltipContent>
              </Tooltip>
            </label>
            <Select value={bankId} onValueChange={applyBank}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BD_BANKS.map((b) => (
                  <SelectItem key={b.id} value={b.id} className={lang === "bn" ? "font-bn" : ""}>
                    {b.name[lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DateField
            label={tr("startDate")}
            value={startDate}
            onChange={setStartDate}
            fromYear={1990}
            toDate={new Date(2100, 11, 31)}
          />
        </div>

        {/* Loan amount */}
        <div className="mt-6 space-y-3">
          <div className="flex items-end justify-between gap-3">
            <NumberInput
              label={tr("loanAmount")}
              value={amountStr}
              onChange={setAmountStr}
              unit="৳"
              error={errKey === "amount_too_small" ? errMessage : null}
            />
            <p className="mb-2 hidden text-xs text-muted-foreground sm:block">{money(amount, 0)}</p>
          </div>
          <Slider
            value={[amount]}
            min={AMOUNT_MIN}
            max={AMOUNT_MAX}
            step={1000}
            onValueChange={(v) => {
              setAmount(v[0]);
              setAmountStr(String(v[0]));
            }}
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{money(AMOUNT_MIN, 0)}</span>
            <span>{money(AMOUNT_MAX, 0)}</span>
          </div>
        </div>

        {/* Rate */}
        <div className="mt-6 space-y-3">
          <NumberInput
            label={tr("rate")}
            value={rateStr}
            onChange={setRateStr}
            unit="%"
            error={errKey === "rate_negative" ? errMessage : null}
          />
          <Slider
            value={[rate]}
            min={RATE_MIN}
            max={RATE_MAX}
            step={0.1}
            onValueChange={(v) => {
              setRate(v[0]);
              setRateStr(String(v[0]));
            }}
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{num(RATE_MIN)}%</span>
            <span>{num(RATE_MAX)}%</span>
          </div>
          {showZeroWarn && (
            <p className={cn("text-xs text-yellow-600 dark:text-yellow-400", lang === "bn" && "font-bn")}>
              ⚠️ {tr("err_zero_warn")}
            </p>
          )}
        </div>

        {/* Tenure */}
        <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto]">
          <div>
            <NumberInput
              label={tr("tenure")}
              value={tenureStr}
              onChange={setTenureStr}
              unit={tenureUnit === "years" ? tr("years") : tr("months")}
              error={errKey === "tenure_zero" ? errMessage : null}
            />
            <Slider
              className="mt-3"
              value={[tenureValue]}
              min={1}
              max={tenureUnit === "years" ? 30 : 360}
              step={1}
              onValueChange={(v) => {
                setTenureValue(v[0]);
                setTenureStr(String(v[0]));
              }}
            />
          </div>
          <div className="self-end">
            <UnitToggle<"years" | "months">
              value={tenureUnit}
              onChange={(u) => {
                if (u === tenureUnit) return;
                if (u === "months") {
                  const m = Math.round(tenureValue * 12);
                  setTenureUnit("months");
                  setTenureValue(m);
                  setTenureStr(String(m));
                } else {
                  const y = Math.max(1, Math.round((tenureValue / 12) * 10) / 10);
                  setTenureUnit("years");
                  setTenureValue(y);
                  setTenureStr(String(y));
                }
              }}
              options={[
                { value: "years", label: tr("years") },
                { value: "months", label: tr("months") },
              ]}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            <input
              type="checkbox"
              checked={bnDigits}
              onChange={(e) => setBnDigits(e.target.checked)}
              className="h-3.5 w-3.5 accent-accent"
            />
            {tr("bnNumerals")}
          </label>
          <Button variant="outline" onClick={reset} className="gap-2">
            <RotateCcw size={14} />
            {tr("reset")}
          </Button>
        </div>

        {/* Results */}
        {result && (
          <motion.div
            ref={printRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mt-8 space-y-6"
          >
            <div className="grid gap-3 md:grid-cols-3">
              <ResultCard
                label={tr("monthlyEMI")}
                value={moneyDisplay(result.emi, lang, bnDigits, 0)}
                accent
              />
              <ResultCard
                label={tr("totalInterest")}
                value={moneyDisplay(result.totalInterest, lang, bnDigits, 0)}
              />
              <ResultCard
                label={tr("totalPayment")}
                value={moneyDisplay(result.totalPayment, lang, bnDigits, 0)}
              />
            </div>

            {/* Donut chart */}
            <div className="grid items-center gap-6 rounded-xl border border-border bg-card p-4 md:grid-cols-[200px_1fr]">
              <div className="h-44">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <RTooltip
                      formatter={(v: number) => moneyDisplay(v, lang, bnDigits, 0)}
                      contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 text-sm">
                <Legend color="var(--accent)" label={tr("principal")} value={moneyDisplay(amount, lang, bnDigits, 0)} lang={lang} />
                <Legend color="var(--muted-foreground)" label={tr("interest")} value={moneyDisplay(result.totalInterest, lang, bnDigits, 0)} lang={lang} />
                <p className={cn("pt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                  {RATES_DISCLAIMER[lang]}
                </p>
              </div>
            </div>

            {/* Schedule */}
            <div className="rounded-xl border border-border bg-card p-3 md:p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("schedule")}</h3>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={downloadCSV} className="gap-1.5">
                    <FileSpreadsheet size={14} />
                    {tr("downloadCsv")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={doPrint} className="gap-1.5">
                    <Printer size={14} />
                    {tr("print")}
                  </Button>
                  <ShareButton title={`${tr("monthlyEMI")}: ${moneyDisplay(result.emi, lang, bnDigits, 0)}`} />
                </div>
              </div>
              <Tabs defaultValue="yearly">
                <TabsList>
                  <TabsTrigger value="yearly" className={lang === "bn" ? "font-bn" : ""}>{tr("yearly")}</TabsTrigger>
                  <TabsTrigger value="monthly" className={lang === "bn" ? "font-bn" : ""}>{tr("monthly")}</TabsTrigger>
                </TabsList>
                <TabsContent value="yearly" className="mt-3">
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("year")}</th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("principalPaid")}</th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("interestPaid")}</th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("balance")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.yearly.map((y) => (
                          <tr key={y.year} className="border-t border-border">
                            <td className="px-3 py-2 tabular-nums">{num(y.year)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{moneyDisplay(y.principalPaid, lang, bnDigits, 0)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{moneyDisplay(y.interestPaid, lang, bnDigits, 0)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{moneyDisplay(y.balance, lang, bnDigits, 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
                <TabsContent value="monthly" className="mt-3">
                  <div className="max-h-96 overflow-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-muted/80 text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
                        <tr>
                          <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("month")}</th>
                          <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("date")}</th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("emi")}</th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("principal")}</th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("interest")}</th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("balance")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.schedule.map((m) => (
                          <tr key={m.index} className="border-t border-border">
                            <td className="px-3 py-2 tabular-nums">{num(m.index)}</td>
                            <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                              {lang === "bn" ? toBengaliNumerals(format(m.date, "MMM yyyy")) : format(m.date, "MMM yyyy")}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{moneyDisplay(m.emi, lang, bnDigits, 0)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{moneyDisplay(m.principal, lang, bnDigits, 0)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{moneyDisplay(m.interest, lang, bnDigits, 0)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{moneyDisplay(m.balance, lang, bnDigits, 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </motion.div>
        )}

        {/* Content */}
        <article className="mt-12 space-y-10">
          <Section title={tr("whatTitle")} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("whatBody")}</p>
          </Section>
          <Section title={tr("formulaTitle")} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("formulaBody")}</p>
          </Section>
          <Section title={tr("reduceTitle")} lang={lang}>
            <ol className={cn("list-decimal space-y-2 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {STR.reduceItems[lang].map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </Section>
          <Section title={tr("flatTitle")} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("flatBody")}</p>
          </Section>
          <Section title={tr("ratesTitle")} lang={lang}>
            <RatesTable lang={lang} />
            <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {RATES_DISCLAIMER[lang]}
            </p>
          </Section>
          <Section title={tr("taxTitle")} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("taxBody")}</p>
          </Section>
        </article>
      </TooltipProvider>
    </CalculatorLayout>
  );
}

/* ---------- Helpers ---------- */

function moneyDisplay(value: number, lang: "en" | "bn", bnDigits: boolean, frac = 0): string {
  // bnDigits forces Bengali numerals even if UI is English
  const useBn = lang === "bn" || bnDigits;
  return formatBDT(Math.round(value * 100) / 100, useBn ? "bn" : "en", frac);
}

function ResultCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "rounded-xl border p-4",
        accent ? "border-accent/40 bg-accent/5" : "border-border bg-card",
      )}
    >
      <p className={cn("text-xs font-semibold uppercase tracking-wide", accent ? "text-accent" : "text-muted-foreground")}>
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums md:text-3xl">{value}</p>
    </motion.div>
  );
}

function Legend({ color, label, value, lang }: { color: string; label: string; value: string; lang: "en" | "bn" }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={cn("inline-flex items-center gap-2", lang === "bn" && "font-bn")}>
        <span className="inline-block h-3 w-3 rounded-sm" style={{ background: color }} />
        {label}
      </span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}

function RatesTable({ lang }: { lang: "en" | "bn" }) {
  const head = {
    bank: { en: "Bank", bn: "ব্যাংক" },
    personal: { en: "Personal", bn: "পার্সোনাল" },
    home: { en: "Home", bn: "হোম" },
    car: { en: "Car", bn: "কার" },
  };
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{head.bank[lang]}</th>
            <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{head.personal[lang]}</th>
            <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{head.home[lang]}</th>
            <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{head.car[lang]}</th>
          </tr>
        </thead>
        <tbody>
          {BD_BANKS.filter((b) => b.id !== "custom").map((b) => (
            <tr key={b.id} className="border-t border-border">
              <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{b.name[lang]}</td>
              <td className="px-3 py-2 text-right tabular-nums">{b.rates.personal != null ? `${lang === "bn" ? toBengaliNumerals(b.rates.personal) : b.rates.personal}%` : "—"}</td>
              <td className="px-3 py-2 text-right tabular-nums">{b.rates.home != null ? `${lang === "bn" ? toBengaliNumerals(b.rates.home) : b.rates.home}%` : "—"}</td>
              <td className="px-3 py-2 text-right tabular-nums">{b.rates.car != null ? `${lang === "bn" ? toBengaliNumerals(b.rates.car) : b.rates.car}%` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
